/**
 * Atlas fog-of-war operations → Path A board overlay.
 *
 * Matches ByteMirror/atlas-vtt fogTypes + Canvas2D compositor semantics:
 * paint = source-over black, erase = destination-out. Transparent = clear map.
 */

export const MAX_FOG_OPS = 256;
export const MAX_FOG_POINTS = 2048;
export const FOG_COLOR = "rgba(0, 0, 0, 1)";

export type FogPoint = { x: number; y: number };

export type FogBrushOp = {
  id: string;
  type: "brush";
  timestamp: number;
  isErasing: boolean;
  points: FogPoint[];
  brushRadius: number;
  offsetX: number;
  offsetY: number;
};

export type FogLassoOp = {
  id: string;
  type: "lasso";
  timestamp: number;
  isErasing: boolean;
  points: FogPoint[];
  offsetX: number;
  offsetY: number;
};

export type FogRectOp = {
  id: string;
  type: "rectangle";
  timestamp: number;
  isErasing: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
  offsetX: number;
  offsetY: number;
};

export type FogOp = FogBrushOp | FogLassoOp | FogRectOp;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parsePoints(raw: unknown): FogPoint[] | null {
  if (!Array.isArray(raw) || raw.length > MAX_FOG_POINTS) return null;
  const points: FogPoint[] = [];
  for (const p of raw) {
    if (!isRecord(p)) return null;
    if (typeof p.x !== "number" || !Number.isFinite(p.x)) return null;
    if (typeof p.y !== "number" || !Number.isFinite(p.y)) return null;
    points.push({ x: p.x, y: p.y });
  }
  return points;
}

/**
 * Parse Atlas `objects.fog` bag into ordered fog ops.
 * Invalid entries are skipped (same soft-fail style as pins).
 */
export function parseAtlasFog(raw: unknown): FogOp[] {
  if (!isRecord(raw)) return [];
  const ops: FogOp[] = [];
  const seen = new Set<string>();

  for (const [key, value] of Object.entries(raw)) {
    if (ops.length >= MAX_FOG_OPS) break;
    if (!isRecord(value)) continue;
    const id =
      typeof value.id === "string" && value.id.trim()
        ? value.id.trim()
        : key.trim();
    if (!id || seen.has(id)) continue;
    const type = value.type;
    if (type !== "brush" && type !== "lasso" && type !== "rectangle") continue;
    const timestamp =
      typeof value.timestamp === "number" && Number.isFinite(value.timestamp)
        ? value.timestamp
        : 0;
    const isErasing = value.isErasing === true;
    const offsetX =
      typeof value.offsetX === "number" && Number.isFinite(value.offsetX)
        ? value.offsetX
        : 0;
    const offsetY =
      typeof value.offsetY === "number" && Number.isFinite(value.offsetY)
        ? value.offsetY
        : 0;

    if (type === "brush") {
      const points = parsePoints(value.points);
      if (!points || points.length === 0) continue;
      const brushRadius =
        typeof value.brushRadius === "number" &&
        Number.isFinite(value.brushRadius) &&
        value.brushRadius > 0
          ? value.brushRadius
          : 50;
      seen.add(id);
      ops.push({
        id,
        type: "brush",
        timestamp,
        isErasing,
        points,
        brushRadius,
        offsetX,
        offsetY,
      });
      continue;
    }

    if (type === "lasso") {
      const points = parsePoints(value.points);
      if (!points || points.length < 3) continue;
      seen.add(id);
      ops.push({
        id,
        type: "lasso",
        timestamp,
        isErasing,
        points,
        offsetX,
        offsetY,
      });
      continue;
    }

    if (
      typeof value.x !== "number" ||
      !Number.isFinite(value.x) ||
      typeof value.y !== "number" ||
      !Number.isFinite(value.y) ||
      typeof value.width !== "number" ||
      !Number.isFinite(value.width) ||
      typeof value.height !== "number" ||
      !Number.isFinite(value.height)
    ) {
      continue;
    }
    seen.add(id);
    ops.push({
      id,
      type: "rectangle",
      timestamp,
      isErasing,
      x: value.x,
      y: value.y,
      width: value.width,
      height: value.height,
      offsetX,
      offsetY,
    });
  }

  ops.sort((a, b) => a.timestamp - b.timestamp);
  return ops;
}

export function parseFogOps(raw: unknown):
  | { ok: true; fog: FogOp[] }
  | { ok: false; error: string } {
  if (raw === undefined) return { ok: true, fog: [] };
  if (!Array.isArray(raw)) return { ok: false, error: "fog must be an array" };
  if (raw.length > MAX_FOG_OPS) {
    return { ok: false, error: `Too many fog ops (max ${MAX_FOG_OPS})` };
  }
  // Re-validate via Atlas bag shape by reconstituting id→op map.
  const bag: Record<string, unknown> = {};
  for (let i = 0; i < raw.length; i++) {
    const op = raw[i];
    if (!isRecord(op) || typeof op.id !== "string" || !op.id.trim()) {
      return { ok: false, error: `fog[${i}] must be an object with id` };
    }
    bag[op.id.trim()] = op;
  }
  return { ok: true, fog: parseAtlasFog(bag) };
}

function renderBrush(
  ctx: CanvasRenderingContext2D,
  op: FogBrushOp,
  scale: number,
): void {
  const { points, brushRadius, offsetX, offsetY } = op;
  if (points.length === 0) return;
  const r = brushRadius * scale;
  ctx.beginPath();
  if (points.length === 1) {
    const p = points[0]!;
    const cx = (p.x + offsetX) * scale;
    const cy = (p.y + offsetY) * scale;
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i]!;
    const p1 = points[i + 1]!;
    const dx = p1.x - p0.x;
    const dy = p1.y - p0.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const steps = Math.max(1, Math.ceil(dist / (brushRadius * 0.4)));
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const cx = (p0.x + dx * t + offsetX) * scale;
      const cy = (p0.y + dy * t + offsetY) * scale;
      ctx.moveTo(cx + r, cy);
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
    }
  }
  ctx.fill();
}

function renderLasso(
  ctx: CanvasRenderingContext2D,
  op: FogLassoOp,
  scale: number,
): void {
  const { points, offsetX, offsetY } = op;
  if (points.length < 3) return;
  ctx.beginPath();
  const p0 = points[0]!;
  ctx.moveTo((p0.x + offsetX) * scale, (p0.y + offsetY) * scale);
  for (let i = 1; i < points.length; i++) {
    const p = points[i]!;
    ctx.lineTo((p.x + offsetX) * scale, (p.y + offsetY) * scale);
  }
  ctx.closePath();
  ctx.fill();
}

function renderRect(
  ctx: CanvasRenderingContext2D,
  op: FogRectOp,
  scale: number,
): void {
  ctx.fillRect(
    (op.x + op.offsetX) * scale,
    (op.y + op.offsetY) * scale,
    op.width * scale,
    op.height * scale,
  );
}

export type FogTokenClear = {
  x: number;
  y: number;
  size?: number;
  visionRadius?: number;
};

/** Paint fog ops onto a canvas sized to mapWidth×mapHeight (CSS pixels via scale). */
export function compositeFogOps(
  ctx: CanvasRenderingContext2D,
  ops: FogOp[],
  mapWidth: number,
  mapHeight: number,
  scale = 0.5,
  tokens?: FogTokenClear[],
): void {
  const w = Math.max(1, Math.round(mapWidth * scale));
  const h = Math.max(1, Math.round(mapHeight * scale));
  if (ctx.canvas.width !== w) ctx.canvas.width = w;
  if (ctx.canvas.height !== h) ctx.canvas.height = h;
  ctx.clearRect(0, 0, w, h);
  const sorted = [...ops].sort((a, b) => a.timestamp - b.timestamp);
  for (const op of sorted) {
    ctx.save();
    ctx.globalCompositeOperation = op.isErasing
      ? "destination-out"
      : "source-over";
    ctx.fillStyle = FOG_COLOR;
    switch (op.type) {
      case "brush":
        renderBrush(ctx, op, scale);
        break;
      case "lasso":
        renderLasso(ctx, op, scale);
        break;
      case "rectangle":
        renderRect(ctx, op, scale);
        break;
      default: {
        const _exhaustive: never = op;
        void _exhaustive;
      }
    }
    ctx.restore();
  }

  // Tokens on the board clear fog around them (erase destination-out hole)
  if (tokens && tokens.length > 0) {
    ctx.save();
    ctx.globalCompositeOperation = "destination-out";
    for (const token of tokens) {
      if (typeof token.x !== "number" || typeof token.y !== "number") continue;
      const tSize = typeof token.size === "number" && token.size > 0 ? token.size : 70;
      // Use token vision/light radius if present; otherwise default to 1.5x token diameter (min 105px)
      const clearRadius =
        typeof token.visionRadius === "number" && token.visionRadius > 0
          ? token.visionRadius
          : Math.max(tSize * 1.5, 105);
      const r = clearRadius * scale;
      const cx = token.x * scale;
      const cy = token.y * scale;

      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}

/** Atlas player view = smoky, slightly transparent fog; GM edit view = half-transparent. */
export function fogOverlayAlpha(role: "gm" | "player"): number {
  return role === "gm" ? 0.45 : 0.82;
}
